import type { TaskSnapshot } from '../stores/types';
import { parseDateSafe } from '../utils/utils';

/** Add a task and walk parentTaskId ancestors into `keep`. */
export function addTaskIdWithAncestors(
  keep: Set<string>,
  taskId: string,
  byId: Map<string, TaskSnapshot>,
): void {
  let current: string | null = String(taskId);
  while (current) {
    if (keep.has(current)) break;
    keep.add(current);
    const parent = byId.get(current);
    current = parent?.parentTaskId ? String(parent.parentTaskId) : null;
  }
}

function asPercent(value: number | null | undefined): number {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return 0;
  const scaled = n <= 1 ? n * 100 : n;
  return Math.max(0, Math.min(100, scaled));
}

/** True when planned finish is before `now` and the task is not complete. */
export function isCriticalPathTaskOverdue(
  task: TaskSnapshot,
  now: Date = new Date(),
): boolean {
  const finish = parseDateSafe(task.finish ?? null);
  if (!finish) return false;
  return finish < now && asPercent(task.percentComplete) < 100;
}

function isTaskOnCriticalPath(
  task: TaskSnapshot,
  isCriticalTask?: (task: { id?: string | number }) => boolean,
): boolean {
  let liveCritical = false;
  if (typeof isCriticalTask === 'function') {
    try {
      liveCritical = !!isCriticalTask({ id: task.id });
    } catch {
      liveCritical = false;
    }
  }
  return !!task.critical || liveCritical;
}

/**
 * Visible set for "Critical path only": critical task ids + ancestors for WBS context.
 * Stored MSP/backend `task.critical` is primary; live DHTMLX `isCriticalTask` is additive
 * so local edits that become critical still appear before a backend recalc.
 */
export function collectCriticalPathVisibleTaskIds(params: {
  allTasks: TaskSnapshot[];
  isCriticalTask?: (task: { id?: string | number }) => boolean;
}): Set<string> {
  const { allTasks, isCriticalTask } = params;
  const byId = new Map(allTasks.map((t) => [String(t.id), t]));
  const keep = new Set<string>();

  for (const task of allTasks) {
    if (isTaskOnCriticalPath(task, isCriticalTask)) {
      addTaskIdWithAncestors(keep, String(task.id), byId);
    }
  }

  return keep;
}

/**
 * Critical-path task ids only (no ancestor-only WBS rows).
 * Used to tint rows without hiding the rest of the schedule.
 */
export function collectCriticalPathTaskIds(params: {
  allTasks: TaskSnapshot[];
  isCriticalTask?: (task: { id?: string | number }) => boolean;
}): Set<string> {
  const { allTasks, isCriticalTask } = params;
  const ids = new Set<string>();
  for (const task of allTasks) {
    if (isTaskOnCriticalPath(task, isCriticalTask)) ids.add(String(task.id));
  }
  return ids;
}

export type CriticalPathSummary = {
  /** Tasks on the critical path (MSP∪live), excluding ancestor-only context rows. */
  criticalCount: number;
  /** Critical-path tasks whose planned finish is past and progress is under 100%. */
  overdueCount: number;
};

/** Counts for the Critical path strip (toggle ON). */
export function summarizeCriticalPath(params: {
  allTasks: TaskSnapshot[];
  isCriticalTask?: (task: { id?: string | number }) => boolean;
  now?: Date;
}): CriticalPathSummary {
  const { allTasks, isCriticalTask, now = new Date() } = params;
  let criticalCount = 0;
  let overdueCount = 0;
  for (const task of allTasks) {
    if (!isTaskOnCriticalPath(task, isCriticalTask)) continue;
    criticalCount += 1;
    if (isCriticalPathTaskOverdue(task, now)) overdueCount += 1;
  }
  return { criticalCount, overdueCount };
}
